// src/app/shared/utils/global-message.util.ts
import { FormGroup } from '@angular/forms';
import { Roll, stationTimeStamps } from '@app/models/knitting/roll';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { getLastProcessingStamp, getMostRecentStamp } from './weight-station-classification.util';

export class KnittingUtil {
  private static messageService: MessageService;
  private static readonly DEFAULT_KEY = 'main';

  public static getComboKey(assetId: string, fabricCode: string, variantCode: string): string {
    return `${assetId}-${fabricCode}-${variantCode}`;
  }

  public static getNextVariantCode(fabricCode: string, controls: FormGroup[]): string {
    const matchingDetails = _.filter(controls, (formGroup) => formGroup.value.fabric?.fabricCode === fabricCode);

    const highestNumber = matchingDetails.reduce((max, formGroup) => {
      const code: string = formGroup.value.variantCode || '';
      const match = code.match(/Entry (\d+)/);
      if (match) {
        const num = parseInt(match[1], 10);
        return Math.max(max, num);
      }
      return max;
    }, 0);

    return `Entry ${highestNumber + 1}`;
  }

  public static getWorkOrderNumber(knittingOrderNumber: string) {
    const parts = _.split(knittingOrderNumber, '-');
    if (parts.length > 1) {
      return parts.slice(0, -1).join('-');
    }
    return knittingOrderNumber;
  }

  public static separateFabricCodeAndVariantCode(comboKey: string): { fabricCode: string; variantCode: string } {
    const lastDashIndex = comboKey.lastIndexOf('-');
    if (lastDashIndex === -1) {
      return { fabricCode: comboKey, variantCode: '' };
    }
    const fabricCode = comboKey.substring(0, lastDashIndex);
    const variantCode = comboKey.substring(lastDashIndex + 1);
    return { fabricCode, variantCode };
  }

  public static getRollTimeStamp(roll: Roll, latest = true): stationTimeStamps | undefined {
    if (!roll.stationTimeStamps || roll.stationTimeStamps.length === 0) {
      return undefined;
    }
    const timestamps = roll.stationTimeStamps;
    return latest ? getLastProcessingStamp(timestamps) : timestamps[0];
  }

  public static getRollWeightInLbs(roll: Roll): number {
    const stationTimeStamp = this.getRollTimeStamp(roll, true);
    if (!_.isNil(stationTimeStamp)) {
      return stationTimeStamp.weightInLbs!;
    }
    return 0;
  }

  /**
   * The roll's current weight: its Final Weight if one is registered, otherwise the most recent
   * station stamp of ANY type — including ParentSplit/ChildSplit from a roll split. Mirrors the
   * backend's RollWeightHelper.GetRollWeight(..., RollWeightType.CurrentWeight).
   *
   * Use this (not getRollWeightInLbs) anywhere the UI needs "what does this roll weigh right now" —
   * bundle totals, roll listings, the default "Weight (lb)" column. getRollWeightInLbs/
   * getLastProcessingStamp intentionally excludes Final Weight and Split stamps and remains correct
   * for contexts that specifically want the pre-final/pre-split weight (e.g. reprocess "First
   * Weight (lb)"). See Bug 5097.
   */
  public static getCurrentWeightInLbs(roll: Roll): number {
    if (roll.finalWeight?.weightInLbs) {
      return roll.finalWeight.weightInLbs;
    }
    return getMostRecentStamp(roll.stationTimeStamps)?.weightInLbs ?? 0;
  }

  /**
   * Determines if a roll is a child roll (created from a split operation).
   * A child roll has more than 2 dashes in its number (e.g., R-001-1, R-001-2).
   * Parent rolls have exactly 2 parts (e.g., R-001).
   */
  public static isChildRoll(rollNumber: string): boolean {
    const parts = rollNumber.split('-');
    return parts.length > 2;
  }

  /**
   * Rolls weighed before the correlative existed have none, so they fall last, ordered by weighing date.
   * The API's batch print applies the same rule — screen and printed stack must not disagree.
   */
  public static sortRollsByFinalWeightOrder(rolls: Roll[]): Roll[] {
    return [...rolls].sort((a, b) => {
      if (a.finalWeightOrder != null && b.finalWeightOrder != null) {
        return a.finalWeightOrder - b.finalWeightOrder;
      }
      if (a.finalWeightOrder != null) {
        return -1;
      }
      if (b.finalWeightOrder != null) {
        return 1;
      }
      const timeA = a.finalWeight?.timeStamp ? new Date(a.finalWeight.timeStamp).getTime() : 0;
      const timeB = b.finalWeight?.timeStamp ? new Date(b.finalWeight.timeStamp).getTime() : 0;
      return timeA - timeB;
    });
  }
}
